import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useNavigate, Link, useLocation } from 'react-router-dom';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children, currentPage }: AdminLayoutProps) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line', path: '/admin/dashboard' },
    { id: 'sales', label: 'Ventas', icon: 'ri-shopping-cart-line', path: '/admin/sales' },
    { id: 'expenses', label: 'Gastos', icon: 'ri-file-list-line', path: '/admin/expenses' },
    { id: 'profits', label: 'Ganancias', icon: 'ri-line-chart-line', path: '/admin/profits' },
    { id: 'customers', label: 'Clientes', icon: 'ri-user-line', path: '/admin/customers' },
    { id: 'employees', label: 'Empleados', icon: 'ri-team-line', path: '/admin/employees' },
    { id: 'payrolls', label: 'Nóminas', icon: 'ri-money-dollar-circle-line', path: '/admin/payrolls' },
    { id: 'suppliers', label: 'Proveedores', icon: 'ri-truck-line', path: '/admin/suppliers' },
    { id: 'images', label: 'Imágenes', icon: 'ri-image-line', path: '/admin/images' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-gradient-to-b from-teal-700 to-teal-900 text-white fixed h-full">
        <div className="p-6 border-b border-teal-600">
          <h1 className="text-2xl font-bold">PUREZA Admin</h1>
          <p className="text-sm text-teal-200 mt-1">Panel de Administración</p>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="mb-4">
            <a
              href="/"
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white text-teal-800 border border-teal-200 hover:bg-teal-50 transition-colors cursor-pointer shadow-sm"
            >
              <i className="ri-home-line text-xl"></i>
              <span className="font-medium">Página Principal</span>
            </a>
          </div>
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.id}>
                <a
                  href={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer ${
                    currentPage === item.id
                      ? 'bg-teal-600 text-white'
                      : 'text-teal-100 hover:bg-teal-600/50'
                  }`}
                >
                  <i className={`${item.icon} text-xl`}></i>
                  <span className="font-medium">{item.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-teal-600">
          <a
            href="/admin/change-password"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-teal-100 hover:bg-teal-600/50 transition-colors cursor-pointer mb-2"
          >
            <i className="ri-lock-password-line text-xl"></i>
            <span className="font-medium">Cambiar Contraseña</span>
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-teal-100 hover:bg-red-600 transition-colors cursor-pointer"
          >
            <i className="ri-logout-box-line text-xl"></i>
            <span className="font-medium">Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar - Mobile */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-teal-700 to-teal-900 text-white z-50 transform transition-transform duration-300 lg:hidden ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-6 border-b border-teal-600 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">PUREZA Admin</h1>
            <p className="text-sm text-teal-200 mt-1">Panel de Administración</p>
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="text-white hover:text-teal-200 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto h-[calc(100vh-200px)]">
          <div className="mb-4">
            <a
              href="/"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white text-teal-800 border border-teal-200 hover:bg-teal-50 transition-colors cursor-pointer shadow-sm"
            >
              <i className="ri-home-line text-xl"></i>
              <span className="font-medium">Página Principal</span>
            </a>
          </div>
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.id}>
                <a
                  href={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer ${
                    currentPage === item.id
                      ? 'bg-teal-600 text-white'
                      : 'text-teal-100 hover:bg-teal-600/50'
                  }`}
                >
                  <i className={`${item.icon} text-xl`}></i>
                  <span className="font-medium">{item.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-teal-600">
          <a
            href="/admin/change-password"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-teal-100 hover:bg-teal-600/50 transition-colors cursor-pointer mb-2"
          >
            <i className="ri-lock-password-line text-xl"></i>
            <span className="font-medium">Cambiar Contraseña</span>
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-teal-100 hover:bg-red-600 transition-colors cursor-pointer"
          >
            <i className="ri-logout-box-line text-xl"></i>
            <span className="font-medium">Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar - Mobile */}
        <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="text-gray-700 hover:text-teal-600 cursor-pointer"
          >
            <i className="ri-menu-line text-2xl"></i>
          </button>
          <h1 className="text-lg font-bold text-gray-900">PUREZA Admin</h1>
          <a href="/" className="text-gray-700 hover:text-teal-600 cursor-pointer">
            <i className="ri-home-line text-2xl"></i>
          </a>
        </div>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
